import { getApiUrl } from '@/services/api';
import type { CategoryOption } from '@/types';
import type {
  AdminCrawler,
  AdminEvent,
  AdminEventPage,
  AdminSession,
  AuditEntry,
  CrawlerRun,
  EventFilters,
  EventPayload,
} from '@/types/admin';

export class AdminApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

const CONFLICT_MESSAGES: Record<string, string> = {
  'already running': 'common:errors.alreadyRunning',
  'crawler disabled': 'common:errors.crawlerDisabled',
};

const FIELD_MESSAGES: Record<string, string> = {
  '必须是文本': 'text',
  '必填': 'required',
  '必须是整数': 'integer',
  '日期格式应为 YYYY-MM-DD': 'date',
  '必须是 http:// 或 https:// 开头的完整链接': 'url',
  '请求体必须是 JSON 对象': 'json',
  '事件类型必须是 annual 或 specific': 'eventType',
  '该月份没有这一天': 'day',
  '结束日期不能早于开始日期': 'dateOrder',
  '不能超过 50 个字符': 'length50',
  '不能超过 200 个字符': 'length200',
  '不能超过 300 个字符': 'length300',
  '不能超过 500 个字符': 'length500',
  '不能超过 2000 个字符': 'length2000',
  '必须在 1 到 12 之间': 'monthRange',
  '必须在 1 到 31 之间': 'dayRange',
  '必须在 1 到 9999 之间': 'yearRange',
};

function fieldMessage(message: unknown): string {
  if (typeof message !== 'string') return 'common:errors.validation';
  const key = FIELD_MESSAGES[message];
  return key ? `common:validation.${key}` : message;
}

// Set by the admin session provider; every write sends it as X-CSRF-Token.
let csrfToken = '';
let onUnauthenticated: () => void = () => {};

export function configureAdminApi(token: string, unauthenticated: () => void) {
  csrfToken = token;
  onUnauthenticated = unauthenticated;
}

async function request<T>(
  path: `/${string}`,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.method && init.method !== 'GET') {
    headers.set('X-CSRF-Token', csrfToken);
  }
  if (init.body) {
    headers.set('Content-Type', 'application/json');
  }
  const response = await fetch(getApiUrl(path), {
    ...init,
    headers,
    credentials: 'same-origin',
  });
  if (response.status === 401) {
    onUnauthenticated();
    throw new AdminApiError(401, 'common:errors.sessionExpired');
  }
  if (response.status === 204) {
    return undefined as T;
  }
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    let fields: Record<string, string> = {};
    if (body?.fields && typeof body.fields === 'object' && !Array.isArray(body.fields)) {
      fields = Object.fromEntries(Object.entries(body.fields).map(([key, value]) => [key, fieldMessage(value)]));
    }
    let message = 'common:errors.requestFailed';
    if (response.status === 403) {
      message = 'common:errors.forbidden';
    }
    if (response.status === 409) {
      let conflict = '';
      if (body && typeof body.error === 'string') {
        conflict = body.error;
      }
      message = CONFLICT_MESSAGES[conflict] ?? 'common:errors.conflict';
    }
    if (response.status === 422) {
      message = 'common:errors.validation';
    }
    throw new AdminApiError(response.status, message, fields);
  }
  return body as T;
}

export function adminLoginUrl(next?: string): string {
  const path = getApiUrl('/api/admin/auth/login');
  if (!next) {
    return path;
  }
  return `${path}?next=${encodeURIComponent(next)}`;
}

export function fetchAdminSession(signal?: AbortSignal): Promise<AdminSession> {
  return request<AdminSession>('/api/admin/session', { signal });
}

export function logoutAdmin(): Promise<void> {
  return request<void>('/api/admin/auth/logout', { method: 'POST' });
}

export function fetchAdminEvents(
  filters: EventFilters,
  signal?: AbortSignal,
): Promise<AdminEventPage> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== '' && value !== undefined) {
      params.set(key, String(value));
    }
  }
  return request<AdminEventPage>(`/api/admin/events?${params.toString()}`, { signal });
}

export function fetchAdminEvent(
  id: number,
  signal?: AbortSignal,
): Promise<{ event: AdminEvent; audit: AuditEntry[] }> {
  return request(`/api/admin/events/${id}`, { signal });
}

export function fetchAdminCategories(signal?: AbortSignal): Promise<string[]> {
  return request<string[]>('/api/admin/categories', { signal });
}

export function fetchAdminCategoryOptions(signal?: AbortSignal): Promise<CategoryOption[]> {
  return request<CategoryOption[]>('/api/admin/category-options', { signal });
}

export function createAdminEvent(payload: EventPayload): Promise<AdminEvent> {
  return request<AdminEvent>('/api/admin/events', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateAdminEvent(id: number, payload: EventPayload): Promise<AdminEvent> {
  return request<AdminEvent>(`/api/admin/events/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export function setAdminEventVisibility(id: number, visible: boolean): Promise<AdminEvent> {
  let action = 'hide';
  if (visible) {
    action = 'unhide';
  }
  return request<AdminEvent>(`/api/admin/events/${id}/${action}`, { method: 'POST' });
}

export function unlockAdminEvent(id: number): Promise<AdminEvent> {
  return request<AdminEvent>(`/api/admin/events/${id}/unlock`, { method: 'POST' });
}

export function fetchAdminAudit(limit = 100, signal?: AbortSignal): Promise<AuditEntry[]> {
  return request<AuditEntry[]>(`/api/admin/audit?limit=${limit}`, { signal });
}

export function fetchAdminCrawlers(signal?: AbortSignal): Promise<AdminCrawler[]> {
  return request<AdminCrawler[]>('/api/admin/crawlers', { signal });
}

export function fetchAdminCrawlerRuns(limit = 20, signal?: AbortSignal): Promise<CrawlerRun[]> {
  return request<CrawlerRun[]>(`/api/admin/crawlers/runs?limit=${limit}`, { signal });
}

/** Starts a crawl in the background; poll fetchAdminCrawlers for the result. */
export function runAdminCrawler(id: string): Promise<CrawlerRun> {
  return request<CrawlerRun>(`/api/admin/crawlers/${encodeURIComponent(id)}/run`, {
    method: 'POST',
  });
}
